import { create } from 'zustand'
import { persist } from 'zustand/middleware'

const chatThemes = {
  default: {
    id: 'default',
    name: 'Default',
    background: 'bg-gray-50 dark:bg-gray-900',
    pattern: null,
    image: null
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean Waves',
    background: 'bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600',
    pattern: 'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E',
    image: null
  },
  sunset: {
    id: 'sunset',
    name: 'Sunset Gradient',
    background: 'bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600',
    pattern: 'data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpolygon points="20 20 40 40 20 40"/%3E%3Cpolygon points="20 20 40 20 40 0 20 0"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E',
    image: null
  },
  forest: {
    id: 'forest',
    name: 'Forest Green',
    background: 'bg-gradient-to-br from-green-400 via-green-500 to-green-700',
    pattern: 'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.15"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3Ccircle cx="10" cy="10" r="4"/%3E%3Ccircle cx="50" cy="10" r="4"/%3E%3Ccircle cx="10" cy="50" r="4"/%3E%3Ccircle cx="50" cy="50" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E',
    image: null
  },
  galaxy: {
    id: 'galaxy',
    name: 'Galaxy Night',
    background: 'bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900',
    pattern: 'data:image/svg+xml,%3Csvg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.2"%3E%3Ccircle cx="40" cy="40" r="2"/%3E%3Ccircle cx="20" cy="20" r="1"/%3E%3Ccircle cx="60" cy="20" r="1"/%3E%3Ccircle cx="20" cy="60" r="1"/%3E%3Ccircle cx="60" cy="60" r="1"/%3E%3Ccircle cx="10" cy="40" r="1"/%3E%3Ccircle cx="70" cy="40" r="1"/%3E%3Ccircle cx="40" cy="10" r="1"/%3E%3Ccircle cx="40" cy="70" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E',
    image: null
  },
  minimal: {
    id: 'minimal',
    name: 'Minimal Light',
    background: 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900',
    pattern: null,
    image: null
  },
  cherry: {
    id: 'cherry',
    name: 'Cherry Blossom',
    background: 'bg-gradient-to-br from-pink-200 via-pink-300 to-rose-400',
    pattern: 'data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.3"%3E%3Cpath d="M30 30c0-5.523-4.477-10-10-10s-10 4.477-10 10 4.477 10 10 10 10-4.477 10-10zm20 0c0-5.523-4.477-10-10-10s-10 4.477-10 10 4.477 10 10 10 10-4.477 10-10z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E',
    image: null
  }
}

export const useChatThemeStore = create(
  persist(
    (set, get) => ({
      // State
      currentTheme: 'default',
      availableThemes: chatThemes,
      isThemePickerOpen: false,

      // Actions
      setTheme: (themeId) => {
        if (chatThemes[themeId]) {
          set({ currentTheme: themeId })
        }
      },

      getCurrentTheme: () => {
        const { currentTheme, availableThemes } = get()
        return availableThemes[currentTheme] || availableThemes.default
      },

      toggleThemePicker: () => {
        set((state) => ({ isThemePickerOpen: !state.isThemePickerOpen }))
      },

      closeThemePicker: () => {
        set({ isThemePickerOpen: false })
      },

      getThemeClasses: () => {
        const theme = get().getCurrentTheme()
        let classes = theme.background
        
        if (theme.pattern) {
          classes += ` [background-image:url('${theme.pattern}')]`
        }
        
        return classes
      }
    }),
    {
      name: 'chat-theme-storage',
      partialize: (state) => ({ currentTheme: state.currentTheme })
    }
  )
)